import React from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import * as elog from '@reverbdotcom/commons/src/elog';
import ErrorDisplay from '../components/discovery/error_display';

// When this event name is logged by elog.error, it feeds into
// the reverb.web.broken_guitars metric defined here https://app.datadoghq.com/logs/metrics.
export const BROKEN_GUITAR_FATAL_ERROR_NAME = 'fatal-fallback-error-boundary';

// elog.error wraps anything without a `message` in `new Error(value)`, which
// turns a route error response into "[object Object]".
function toError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (isRouteErrorResponse(error)) {
    return new Error(`${error.status} ${error.statusText}: ${error.data}`);
  }

  return new Error(String(error));
}

function logBrokenGuitar(error: unknown) {
  // Do not log 4xx routing result errors as broken guitars.
  if (isRouteErrorResponse(error) && error.status < 500) return;

  elog.error(
    BROKEN_GUITAR_FATAL_ERROR_NAME,
    {
      url: window?.location?.href,
      ...(typeof error === 'object' ? error : null),
    },
    toError(error),
  );
}

/**
 * The error boundary for reverb.com's routes.
 *
 * react-router catches route errors in its own boundary and renders this in
 * place of the failed subtree rather than re-throwing, so nothing reaches the
 * ErrorBoundary around RouterProvider and nothing there logs. Logging on mount
 * here keeps the elog event tied to the display: one fires whenever the other
 * renders.
 *
 * Give any route that needs its own ErrorBoundary this component, or it will
 * render an error display without reporting it.
 */
export default function RouteErrorDisplay() {
  const error = useRouteError();

  React.useEffect(() => {
    logBrokenGuitar(error);
  }, [error]);

  return <ErrorDisplay />;
}
